import { useState, useEffect, useCallback, useRef } from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StarRatingProps {
  value: number;
  onChange?: (value: number) => void;
  readonly?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  showLabel?: boolean;
}

export function StarRating({ 
  value, 
  onChange, 
  readonly = false, 
  size = 'md',
  className,
  showLabel = true
}: StarRatingProps) {
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const starsRef = useRef<HTMLDivElement>(null);

  // Responsive star sizes - optimized for mobile
  const sizeClasses = {
    xs: 'w-4 h-4 sm:w-5 sm:h-5', // Extra small for tight mobile grids
    sm: 'w-5 h-5 sm:w-6 sm:h-6', // Smaller on mobile, larger on desktop
    md: 'w-6 h-6 sm:w-8 sm:h-8', // Responsive sizing
    lg: 'w-7 h-7 sm:w-9 sm:h-9'  // Responsive sizing
  };

  const paddingClasses = {
    xs: 'gap-0.5', // Small gap for extra small to prevent cramping
    sm: 'gap-0.5 sm:gap-1', // Tighter gap on mobile
    md: 'gap-1 sm:gap-1.5',
    lg: 'gap-1 sm:gap-1.5'
  };

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (readonly || !onChange) return;

    const currentValue = hoverValue ?? value;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        e.preventDefault();
        onChange(Math.min(5, currentValue + 0.5));
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        e.preventDefault();
        onChange(Math.max(0, currentValue - 0.5));
        break;
      case 'Home':
        e.preventDefault();
        onChange(0);
        break;
      case 'End':
        e.preventDefault();
        onChange(5);
        break;
      default:
        // Ignore other keys
        break;
    }
  }, [readonly, onChange, value, hoverValue]);

  // Keyboard events handled locally via onKeyDown on the container.


  // Calculate rating from mouse position
  const getRatingFromPosition = useCallback((clientX: number): number => {
    if (!containerRef.current) return 0;
    
    const rect = containerRef.current.getBoundingClientRect();
    const containerWidth = rect.width;
    const relativeX = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, relativeX / containerWidth));
    const rawRating = percentage * 5;
    
    // Round to nearest 0.5
    return Math.round(rawRating * 2) / 2;
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || readonly) return;
    
    const newRating = getRatingFromPosition(e.clientX);
    setHoverValue(newRating);
    onChange?.(newRating);
  }, [isDragging, readonly, getRatingFromPosition, onChange]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setHoverValue(null);
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = 'none'; // Prevent text selection while dragging
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleStarClick = (starValue: number, isHalf: boolean = false) => {
    if (readonly || !onChange) return;
    const newValue = isHalf ? starValue - 0.5 : starValue;
    
    // Toggle off if clicking same rating - allows clearing
    if (newValue === value) {
      onChange(0);
    } else {
      onChange(newValue);
    }
  };

  const handleStarHover = (starValue: number, isHalf: boolean = false) => {
    if (readonly || isDragging) return;
    const newValue = isHalf ? starValue - 0.5 : starValue;
    setHoverValue(newValue);
  };

  const handleMouseLeave = () => {
    if (readonly || isDragging) return;
    setHoverValue(null);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (readonly || !onChange) return;

    // Defer entering drag until the pointer moves beyond a small threshold
    const startX = e.clientX;
    const threshold = 5; // px

    const onMove = (ev: MouseEvent) => {
      if (Math.abs(ev.clientX - startX) > threshold) {
        setIsDragging(true);
        document.removeEventListener('mousemove', onMove);
      }
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const getStarFill = (starIndex: number) => {
    const currentValue = hoverValue ?? value;
    const starValue = starIndex + 1;
    
    if (currentValue >= starValue) {
      return 'full';
    } else if (currentValue >= starValue - 0.5) {
      return 'half';
    } else {
      return 'empty';
    }
  };

  const currentRating = hoverValue ?? value;

  return (
    <div 
      ref={containerRef}
      className={cn(
        "flex items-center flex-nowrap overflow-hidden",
        paddingClasses[size],
        !readonly && "select-none",
        className
      )}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
      role={readonly ? undefined : "slider"}
      aria-valuemin={readonly ? undefined : 0}
      aria-valuemax={readonly ? undefined : 5}
      aria-valuenow={readonly ? undefined : currentRating}
      aria-label={readonly ? undefined : `Rating: ${currentRating} out of 5 stars. ${value > 0 ? 'Click same star to clear.' : ''}`}
      tabIndex={readonly ? -1 : 0}
      style={{ minHeight: readonly ? 'auto' : (size === 'xs' ? '28px' : '44px') }} // Smaller hit target for xs
    >
      <div ref={starsRef} className="flex items-center flex-shrink-0">
        {Array.from({ length: 5 }).map((_, index) => {
          const starValue = index + 1;
          const fill = getStarFill(index);
          
          return (
            <div key={index} className="relative">
              <button
                type="button"
                className={cn(
                  "relative transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring rounded-sm p-0.5 sm:p-1",
                  "drop-shadow-sm", // Subtle shadow
                  !readonly && "cursor-pointer hover:scale-105 active:scale-95",
                  readonly && "cursor-default pointer-events-none",
                  // Responsive hit targets - smaller on mobile for xs size
                  !readonly && size === 'xs' && "min-w-[24px] min-h-[24px] sm:min-w-[32px] sm:min-h-[32px] flex items-center justify-center",
                  !readonly && size !== 'xs' && "min-w-[36px] min-h-[36px] sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center"
                )}
                onClick={() => !readonly && handleStarClick(starValue)}
                onMouseEnter={() => !readonly && handleStarHover(starValue)}
                disabled={readonly}
                tabIndex={-1} // Parent handles keyboard navigation
                aria-hidden="true"
              >
                {/* Background star (empty outline) - always show for half stars */}
                {fill === 'half' && (
                  <Star 
                    className={cn(
                      sizeClasses[size],
                      "transition-all duration-200",
                      "text-[hsl(var(--star-outline))] fill-none stroke-2"
                    )}
                  />
                )}
                
                {/* Full or main star */}
                <Star 
                  className={cn(
                    sizeClasses[size],
                    "transition-all duration-200",
                    fill === 'empty' 
                      ? "text-[hsl(var(--star-outline))] fill-none stroke-2"
                      : fill === 'full'
                      ? cn(
                          hoverValue !== null 
                            ? "text-[hsl(var(--star-hover))] fill-[hsl(var(--star-hover))] stroke-[hsl(var(--star-outline))]" 
                            : "text-[hsl(var(--star-color))] fill-[hsl(var(--star-color))] stroke-[hsl(var(--star-outline))]",
                          "stroke-1"
                        )
                      : "hidden" // Hide for half to let overlay handle it
                  )}
                />
                
                {/* Half star overlay - clip left half */}
                {fill === 'half' && (
                  <div 
                    className="absolute inset-0 overflow-hidden flex items-center justify-center p-1" 
                    style={{ clipPath: 'inset(0 50% 0 0)' }}
                  >
                    <Star 
                      className={cn(
                        sizeClasses[size], 
                        hoverValue !== null
                          ? "text-[hsl(var(--star-hover))] fill-[hsl(var(--star-hover))] stroke-[hsl(var(--star-outline))]"
                          : "text-[hsl(var(--star-color))] fill-[hsl(var(--star-color))] stroke-[hsl(var(--star-outline))]",
                        "stroke-1 transition-all duration-200"
                      )}
                    />
                  </div>
                )}
              </button>
              
              {/* Half-star click area (left half) - only for non-drag interactions */}
              {!readonly && !isDragging && (
                <button
                  type="button"
                  className="absolute inset-0 w-1/2 h-full z-10 cursor-pointer focus:outline-none"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStarClick(starValue, true);
                  }}
                  onMouseEnter={() => handleStarHover(starValue, true)}
                  tabIndex={-1}
                  aria-hidden="true"
                />
              )}
            </div>
          );
        })}
      </div>
      
      {/* Rating label */}
      {showLabel && !readonly && value > 0 && (
        <span className="ml-3 text-sm text-muted-foreground font-medium whitespace-nowrap">
          {currentRating.toFixed(1)} / 5
        </span>
      )}
    </div>
  );
}